/*** The four reading orders of a tensions step (#89, REU89-D7)
 *
 * Pure on purpose: this is where the traps are. The screen resolves names and
 * draws; everything that decides WHERE a tension lands is decided here and
 * unit-tested next door.
 *
 * 🔑 Nothing here ever writes: the agenda (`threadsIds`) keeps the order the
 * free view wrote by hand, and the three other modes are lenses (REU89-D9).
 */

import {
  MeetingThreadsSortMode,
  ThreadWeather,
  ThreadsWeather,
  threadWeatherRank,
} from './threadWeather'

// A group of the list. `key` is a circle id, a member id, or a weather; the
// screen turns it into a name. `empty` marks a member with no tension: the
// round shows them all the same, announced empty (REU89-D11).
export interface MeetingThreadsGroup<T> {
  key: string
  threads: T[]
  empty: boolean
}

export interface MeetingThreadsGrouping<T> {
  // Empty in the free and priority orders: there the list stays flat.
  groups: MeetingThreadsGroup<T>[]
  // The flat list to draw when there is no group (free and priority orders).
  flat: T[]
  // Settled tensions, always last and out of every group (REU89-D14).
  closed: T[]
}

export interface MeetingThreadsSortInput<T> {
  // The tensions in agenda order — the order the free view wrote.
  threads: T[]
  mode: MeetingThreadsSortMode
  weather: ThreadsWeather
  // The meeting's circle FIRST, then its roles in the circle's own order
  // (REU89-D10): what concerns the collective is settled before what concerns
  // one role.
  circlesOrder: string[]
  // The round's order, participants included even without a tension.
  membersOrder: string[]
  idOf: (thread: T) => string
  circleIdOf: (thread: T) => string | undefined
  memberIdOf: (thread: T) => string | undefined
  isClosed: (thread: T) => boolean
}

// The round's order: what was inherited first, newcomers at the end.
//
// A participant who was not there last time has no place in the inherited
// order; appending them keeps yesterday's order recognisable, and the
// facilitator moves them in three seconds (REU89-D11).
export function orderMembers(
  participantsIds: string[],
  savedOrder?: string[]
): string[] {
  const present = new Set(participantsIds)
  const kept = (savedOrder ?? []).filter((id) => present.has(id))
  const known = new Set(kept)
  return [...kept, ...participantsIds.filter((id) => !known.has(id))]
}

function weatherOf(
  weather: ThreadsWeather,
  threadId: string
): ThreadWeather | undefined {
  return weather[threadId]
}

// Sort inside a group by weather, ties broken by the agenda order.
//
// The agenda order is the tiebreaker rather than a title or a date: it is the
// only order a human posed, so two showers stay where the facilitator put them.
function byWeather<T>(threads: T[], input: MeetingThreadsSortInput<T>): T[] {
  const rank = new Map(threads.map((t, i) => [input.idOf(t), i]))
  return [...threads].sort((a, b) => {
    const diff =
      threadWeatherRank(weatherOf(input.weather, input.idOf(a))) -
      threadWeatherRank(weatherOf(input.weather, input.idOf(b)))
    return diff !== 0
      ? diff
      : (rank.get(input.idOf(a)) ?? 0) - (rank.get(input.idOf(b)) ?? 0)
  })
}

export function groupMeetingThreads<T>(
  input: MeetingThreadsSortInput<T>
): MeetingThreadsGrouping<T> {
  const closed = input.threads.filter(input.isClosed)
  const open = input.threads.filter((t) => !input.isClosed(t))
  const empty: MeetingThreadsGroup<T>[] = []

  if (input.mode === MeetingThreadsSortMode.Free) {
    return { groups: empty, flat: open, closed }
  }

  // One list, no heading per weather (Yohan, 17/09, #106): the badge on each
  // line already says it. An unclassified tension ranks as a shower without
  // anything being written for it (REU89-D5, threadWeatherRank).
  if (input.mode === MeetingThreadsSortMode.Priority) {
    return { groups: empty, flat: byWeather(open, input), closed }
  }

  // By role, by member: the key list drives the order, so a group that holds
  // nothing still has its place when the mode asks for it.
  const byKey = new Map<string, T[]>()
  for (const thread of open) {
    const key =
      input.mode === MeetingThreadsSortMode.Role
        ? input.circleIdOf(thread)
        : input.memberIdOf(thread)
    if (!key) continue
    const list = byKey.get(key)
    if (list) list.push(thread)
    else byKey.set(key, [thread])
  }

  const order =
    input.mode === MeetingThreadsSortMode.Role
      ? input.circlesOrder
      : input.membersOrder
  const groups: MeetingThreadsGroup<T>[] = []
  for (const key of order) {
    const threads = byKey.get(key) ?? []
    byKey.delete(key)
    // A role with nothing to say is not shown; a participant is (REU89-D11).
    if (threads.length === 0 && input.mode === MeetingThreadsSortMode.Role) {
      continue
    }
    groups.push({
      key,
      threads: byWeather(threads, input),
      empty: threads.length === 0,
    })
  }

  // What the order did not name comes last rather than disappearing: a role
  // that left the circle, or a member who is no longer a participant, must
  // never silently drop a tension out of the meeting.
  for (const [key, threads] of byKey) {
    groups.push({ key, threads: byWeather(threads, input), empty: false })
  }

  return { groups, flat: [], closed }
}
